// kan-111 AI Reference Taker — candidate References tab (tab 6) section.
// Rendered above <ReferenceListing>; self-gates on the 'AI_REFERENCE_TAKER'
// Module Access feature (client-side gate, D9 — My Earning precedent).
// packages block
import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import AiReferenceTrailModal from "../../settings/AiReferences/AiReferenceTrailModal";
import {
  Button,
  Collapse,
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
// components block
import Loader from "../../../components/layout/Loader";
import ConfirmPopUp from "../../../components/ConfirmPopUp";
import InputField from "../../../components/layout/InputField";
import {
  RefBadge,
  RefBadgeTone,
  RefContactCell,
  RefDateCell,
  RefEmptyRow,
  RefIconButton,
  RefNameCell,
  RefSectionHeader,
  RefTableHead,
  HistoryIcon,
  PenIcon,
  TrashIcon,
  formatRefDate,
  formatRefTime,
} from "./ReferenceTableParts";
// utils, constants and graphql block
import { AppContext } from "../../../context";
import { EMAIL_PATTERN, PHONE_PATTERN, ROLES } from "../../../utils/constant";
import CLOSE_ICON from "../../../assets/images/close-icon.svg";
import { MY_MODULE_FEATURES } from "../../moduleAccess-settings/gql";
import {
  CREATE_REFERENCE_CONTACT,
  DELETE_REFERENCE_CONTACT,
  ENQUEUE_AI_REFERENCE,
  GET_AI_REFERENCE_ANSWERS,
  GET_AI_REFERENCE_CANDIDATE_QUESTIONS,
  GET_AI_REFERENCE_ENTRIES_FOR_CANDIDATE,
  UPDATE_REFERENCE_CONTACT,
} from "./gql/aiReference";
import AiReferenceQuestionsModal from "./AiReferenceQuestionsModal";
import {
  AiReferenceCandidateQuestionSet,
  questionPillLabel,
} from "./aiReferenceQuestionsUtils";

const AI_REFERENCE_FEATURE_KEY = "AI_REFERENCE_TAKER";

// Mirrors AiReferenceAccessService.PRIVILEGED_ROLES on the API.
const AI_REFERENCE_MANAGE_ROLES = [ROLES.ADMIN, ROLES.EXECUTIVE, ROLES.HR];

const AI_REFERENCE_FORBIDDEN_MESSAGE =
  "Only the candidate owner, Admin, Executive or HR can manage AI references";

const isForbiddenError = (error: any) =>
  (error?.graphQLErrors ?? []).some(
    (e: any) =>
      e?.extensions?.code === "FORBIDDEN" ||
      e?.extensions?.response?.statusCode === 403 ||
      e?.extensions?.exception?.status === 403
  );

const AI_REFERENCE_COLUMNS = [
  "Reference",
  "Contact",
  "AI Status",
  "Last Activity",
  "Action",
];

// GraphQL returns enum KEYS. A missing status (never sent) renders as NEW.
const AI_STATUS_META: Record<string, { label: string; tone: RefBadgeTone }> = {
  NEW: { label: "Not Sent", tone: "neutral" },
  SEND_OUTREACH: { label: "Queued", tone: "info" },
  IN_QUEUE: { label: "Queued", tone: "info" },
  OUTREACH_SENT: { label: "Outreach Sent", tone: "info" },
  AWAITING_RESPONSE: { label: "Awaiting Response", tone: "info" },
  SCHEDULED: { label: "Call Scheduled", tone: "info" },
  CALL_COMPLETED: { label: "Processing Call", tone: "info" },
  COMPLETED: { label: "Completed", tone: "active" },
  NEEDS_HUMAN_OUTREACH: { label: "Needs Human", tone: "warn" },
  DECLINED: { label: "Declined", tone: "danger" },
  CLOSED: { label: "Closed", tone: "neutral" },
};

const NEEDS_HUMAN_REASON_LABEL: Record<string, string> = {
  NO_EMAIL: "No email",
  SEND_FAILED: "Email failed to send",
  ACTIVE_DECLINE: "Contact asked for a human",
  NO_RESPONSE: "No response",
  UNREACHABLE_MISSED_AGAIN: "Missed the call again",
  SILENT_CALL: "Silent call",
};

const DECLINE_REASON_LABEL: Record<string, string> = {
  COMPANY_POLICY: "Company policy",
  NOT_FAMILIAR_ENOUGH: "Not familiar enough",
  PREFER_NOT_TO_PARTICIPATE: "Prefers not to participate",
};

// With NEW / null (never sent), these mirror the server's
// AI_REFERENCE_ENQUEUEABLE_STATUSES.
const RESENDABLE_STATUSES = ["DECLINED", "NEEDS_HUMAN_OUTREACH"];

// mirrors the API's AI_REFERENCE_IN_FLIGHT_STATUSES
const IN_FLIGHT_STATUSES = [
  "SEND_OUTREACH",
  "IN_QUEUE",
  "OUTREACH_SENT",
  "AWAITING_RESPONSE",
  "SCHEDULED",
  "CALL_COMPLETED",
];

const isInFlight = (row?: { aiStatus?: string | null } | null) =>
  !!row?.aiStatus && IN_FLIGHT_STATUSES.includes(row.aiStatus);

const REF_LIST_QUERIES = ["AllReferenceList", "AiReferenceEntriesForCandidate"];

type AiReferenceRow = {
  referenceId: string;
  referenceName?: string | null;
  title?: string | null;
  company?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  relationshipToCandidate?: string | null;
  referenceStatus?: string | null;
  entryId?: string | null;
  aiStatus?: string | null;
  needsHumanReason?: string | null;
  declineReason?: string | null;
  declineNote?: string | null;
  lastOutreachAt?: string | null;
  completedAt?: string | null;
  closedAt?: string | null;
  lastActivityAt?: string | null;
  requestedBy?: { id: string; firstName?: string; lastName?: string } | null;
};

const AiStatusPill = ({ row }: { row: AiReferenceRow }) => {
  const status = row.aiStatus || "NEW";
  const meta: { label: string; tone: RefBadgeTone } = AI_STATUS_META[
    status
  ] ?? { label: status, tone: "neutral" };
  const detail =
    status === "NEEDS_HUMAN_OUTREACH" && row.needsHumanReason
      ? NEEDS_HUMAN_REASON_LABEL[row.needsHumanReason] ?? row.needsHumanReason
      : status === "DECLINED" && row.declineReason
        ? DECLINE_REASON_LABEL[row.declineReason] ?? row.declineReason
        : null;

  return (
    <RefBadge tone={meta.tone} detail={detail}>
      {meta.label}
    </RefBadge>
  );
};

const lastActivityLine = (value?: string | null): string | null => {
  const parts = [formatRefDate(value), formatRefTime(value)].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
};

const requestedByLine = (row: AiReferenceRow): string | null => {
  const name = [row.requestedBy?.firstName, row.requestedBy?.lastName]
    .map((p) => (p ?? "").trim())
    .filter(Boolean)
    .join(" ");
  return name ? `by ${name}` : null;
};

const GearIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

// ---------------------------------------------------------------------------
// Add Reference Contact modal
// ---------------------------------------------------------------------------

type ContactFormValues = {
  referenceName: string;
  title?: string;
  company?: string;
  email: string;
  phoneNumber: string;
  relationshipToCandidate?: string;
};

const AddReferenceContactModal = ({
  isOpen,
  toggle,
  candidateId,
  onCreated,
  editRow = null,
}: {
  isOpen: boolean;
  toggle: () => void;
  candidateId: string;
  onCreated: () => void;
  // When set, the modal edits this contact instead of adding a new one.
  editRow?: AiReferenceRow | null;
}) => {
  const isEdit = !!editRow;
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<ContactFormValues>();

  const [createContact, { loading: createLoading }] = useMutation(
    CREATE_REFERENCE_CONTACT,
    { refetchQueries: REF_LIST_QUERIES }
  );
  const [updateContact, { loading: updateLoading }] = useMutation(
    UPDATE_REFERENCE_CONTACT,
    { refetchQueries: REF_LIST_QUERIES }
  );
  const loading = createLoading || updateLoading;

  useEffect(() => {
    if (isOpen) {
      reset({
        referenceName: editRow?.referenceName ?? "",
        title: editRow?.title ?? "",
        company: editRow?.company ?? "",
        email: editRow?.email ?? "",
        phoneNumber: editRow?.phoneNumber ?? "",
        relationshipToCandidate: editRow?.relationshipToCandidate ?? "",
      });
    }
    // eslint-disable-next-line
  }, [isOpen, editRow?.referenceId]);

  const onSubmit = async (values: ContactFormValues) => {
    const fields = {
      referenceName: values.referenceName?.trim(),
      title: values.title?.trim() || null,
      company: values.company?.trim() || null,
      email: values.email?.trim().toLowerCase(),
      phoneNumber: values.phoneNumber?.trim(),
      relationshipToCandidate: values.relationshipToCandidate?.trim() || null,
    };
    try {
      if (editRow) {
        const res = await updateContact({
          variables: { referenceId: editRow.referenceId, input: fields },
        });
        if (res?.data?.updateReferenceContact) {
          toast.success("Reference contact updated!");
          onCreated();
          toggle();
        }
        return;
      }
      const res = await createContact({
        variables: { input: { candidateId, ...fields } },
      });
      if (res?.data?.createReferenceContact) {
        toast.success("Reference contact added!");
        onCreated();
        toggle();
      }
    } catch (error: any) {
      toast.error(
        isForbiddenError(error)
          ? AI_REFERENCE_FORBIDDEN_MESSAGE
          : error?.message ||
              (isEdit
                ? "Could not update the reference contact."
                : "Could not add the reference contact.")
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      className="revampDialog revampDialogWidth add-reference-dialog"
    >
      <div className="modalHeaderCustom d-flex align-items-center justify-content-between">
        <h5 className="m-0">
          {isEdit ? "Edit Reference Contact" : "Add Reference Contact"}
        </h5>
        <Button className="p-0 bg-transparent border-0" onClick={toggle}>
          <img src={CLOSE_ICON} alt="close" />
        </Button>
      </div>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody className="modalBodyScroll candidateInterview">
          <InputField
            label="Reference Name"
            inputtype="text"
            isRequired={true}
            inputid="aiRefReferenceName"
            placeholder="Jessica Hasson"
            {...register("referenceName", {
              required: { value: true, message: "Reference Name is required!" },
              validate: (v) =>
                !!v?.trim() || "Reference Name is required!",
            })}
            errMsg={errors?.referenceName?.message as any}
          />

          <InputField
            label="Title"
            inputtype="text"
            inputid="aiRefTitle"
            placeholder="Operations Manager"
            {...register("title", { required: false })}
          />

          <InputField
            label="Company"
            inputtype="text"
            inputid="aiRefCompany"
            placeholder="Realty Income Corporation"
            {...register("company", { required: false })}
          />

          <InputField
            label="Email"
            inputtype="text"
            isRequired={true}
            inputid="aiRefEmail"
            placeholder="someone@example.com"
            {...register("email", {
              required: { value: true, message: "Email is required!" },
              pattern: { value: EMAIL_PATTERN, message: "Email is not valid!" },
            })}
            errMsg={errors?.email?.message as any}
          />

          <InputField
            label="Phone Number"
            inputtype="text"
            isRequired={true}
            inputid="aiRefPhoneNumber"
            placeholder="(207)555-0119"
            {...register("phoneNumber", {
              required: { value: true, message: "Phone Number is required!" },
              pattern: {
                value: PHONE_PATTERN,
                message: "Phone Number is not valid!",
              },
              validate: (v) =>
                (v || "").replace(/\D/g, "").length >= 10 ||
                "Phone Number must have at least 10 digits!",
            })}
            errMsg={errors?.phoneNumber?.message as any}
          />

          <InputField
            label="Relationship to Candidate"
            inputtype="text"
            inputid="aiRefRelationship"
            placeholder="Former supervisor"
            {...register("relationshipToCandidate", { required: false })}
          />
        </ModalBody>

        <ModalFooter className="pt-3">
          <div className="d-flex align-items-center">
            <button
              type="button"
              onClick={toggle}
              className="buttonPrimary mr-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="buttonGenericStyle filledButton"
              disabled={loading}
            >
              {loading && <Spinner size="sm" className="mr-2" />}
              {isEdit ? "Save" : "Add"}
            </button>
          </div>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// View answers modal
// ---------------------------------------------------------------------------

const AiReferenceAnswersModal = ({
  row,
  toggle,
}: {
  row: AiReferenceRow | null;
  toggle: () => void;
}) => {
  const [showTranscript, setShowTranscript] = useState(false);
  const [getAnswers, { data, loading, error }] = useLazyQuery(
    GET_AI_REFERENCE_ANSWERS,
    { fetchPolicy: "network-only" }
  );

  useEffect(() => {
    if (row?.entryId) {
      setShowTranscript(false);
      getAnswers({ variables: { referenceEntryId: row.entryId } });
    }
    // eslint-disable-next-line
  }, [row?.entryId]);

  const result = data?.aiReferenceAnswers;
  const answers = [...(result?.answers ?? [])].sort(
    (a: any, b: any) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0)
  );

  return (
    <Modal
      isOpen={!!row}
      toggle={toggle}
      className="revampDialog revampDialogWidth add-reference-dialog"
      scrollable
    >
      <div className="modalHeaderCustom d-flex align-items-center justify-content-between">
        <h5 className="m-0">
          AI Reference — {row?.referenceName ?? "Reference"}
        </h5>
        <Button className="p-0 bg-transparent border-0" onClick={toggle}>
          <img src={CLOSE_ICON} alt="close" />
        </Button>
      </div>

      <ModalBody className="modalBodyScroll">
        {loading && <Loader loaderClass="sm-height" />}
        {!loading && error && (
          <p className="text-danger">Could not load answers: {error.message}</p>
        )}
        {!loading && result && (
          <>

            <div className="mb-3">
              <h6>Answers</h6>
              {answers.length ? (
                answers.map((a: any) => (
                  <div key={a.sortOrder} className="mb-3">
                    <div>
                      <strong>{a.label}</strong>
                    </div>
                    {a.prompt && (
                      <small className="text-muted d-block mb-1">
                        {a.prompt}
                      </small>
                    )}
                    <p className="rich-text-paragraph mb-0">
                      {a.answer || "--"}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-muted mb-0">No answers recorded yet.</p>
              )}
            </div>

            {result.recordingUrl && (
              <div className="mb-3">
                <h6>Recording</h6>
                <a
                  href={result.recordingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Listen to the call recording
                </a>
              </div>
            )}

            {result.transcript && (
              <div>
                <Button
                  className="border-0 bg-transparent color-default p-0"
                  size="sm"
                  onClick={() => setShowTranscript(!showTranscript)}
                >
                  {showTranscript ? "Hide transcript" : "Show transcript"}
                </Button>
                <Collapse isOpen={showTranscript}>
                  <pre
                    className="mt-2 p-2 border rounded"
                    style={{ whiteSpace: "pre-wrap", fontSize: 12 }}
                  >
                    {result.transcript}
                  </pre>
                </Collapse>
              </div>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter className="pt-3">
        <button type="button" onClick={toggle} className="buttonPrimary">
          Close
        </button>
      </ModalFooter>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

const AiReferenceSectionBody = ({ candidateId, candidate }: any) => {
  const { user, userRoles } = useContext(AppContext);
  const [addOpen, setAddOpen] = useState(false);
  const [confirmRow, setConfirmRow] = useState<AiReferenceRow | null>(null);
  const [answersRow, setAnswersRow] = useState<AiReferenceRow | null>(null);
  const [trailRow, setTrailRow] = useState<AiReferenceRow | null>(null);
  // editRow is kept after close so the modal doesn't flip to "Add" mid-fade.
  const [editRow, setEditRow] = useState<AiReferenceRow | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState<AiReferenceRow | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [editPermissions, setEditPermissions] = useState(true);
  const [questionsOpen, setQuestionsOpen] = useState(false);

  const { data, loading, refetch } = useQuery(
    GET_AI_REFERENCE_ENTRIES_FOR_CANDIDATE,
    {
      variables: { candidateId },
      skip: !candidateId,
      fetchPolicy: "cache-and-network",
    }
  );
  const {
    data: questionsData,
    loading: questionsLoading,
    error: questionsError,
  } = useQuery(GET_AI_REFERENCE_CANDIDATE_QUESTIONS, {
    variables: { candidateId },
    // The API asserts "can manage this candidate's references" on this query,
    // so only ask once the client-side permission has resolved to allowed
    // (editPermissions starts true and flips to false for managers).
    skip: !candidateId || editPermissions,
    fetchPolicy: "cache-and-network",
  });
  const questionSet: AiReferenceCandidateQuestionSet | null =
    questionsData?.aiReferenceCandidateQuestions ?? null;
  const [enqueue, { loading: enqueueLoading }] = useMutation(
    ENQUEUE_AI_REFERENCE
  );
  const [deleteContact, { loading: deleteLoading }] = useMutation(
    DELETE_REFERENCE_CONTACT,
    { refetchQueries: REF_LIST_QUERIES }
  );

  // Same rule the API enforces (AiReferenceAccessService): Admin / Executive /
  // HR, or the candidate's recruiting owner.
  useEffect(() => {
    if (!candidate) return;
    const privileged = AI_REFERENCE_MANAGE_ROLES.some((role) =>
      userRoles?.includes(role)
    );
    const isOwner =
      !!user?.id && user?.id === candidate?.recruiterOwner?.id;
    setEditPermissions(!(privileged || isOwner));
    // eslint-disable-next-line
  }, [candidate, user, userRoles]);

  const rows: AiReferenceRow[] = data?.aiReferenceEntriesForCandidate ?? [];

  const confirmEnqueue = async () => {
    if (!confirmRow) return;
    setSendingId(confirmRow.referenceId);
    try {
      const res = await enqueue({
        variables: { referenceId: confirmRow.referenceId },
      });
      if (res?.data?.enqueueAiReference) {
        toast.success(
          `${confirmRow.referenceName || "Reference"} sent to the AI Reference Taker!`
        );
        refetch();
      }
    } catch (error: any) {
      toast.error(
        isForbiddenError(error)
          ? AI_REFERENCE_FORBIDDEN_MESSAGE
          : error?.message || "Could not send to the AI Reference Taker."
      );
    } finally {
      setSendingId(null);
      setConfirmRow(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteRow) return;
    try {
      const res = await deleteContact({
        variables: { referenceId: deleteRow.referenceId },
      });
      if (res?.data?.deleteReferenceContact) {
        toast.success("Reference contact deleted");
      }
    } catch (error: any) {
      toast.error(
        isForbiddenError(error)
          ? AI_REFERENCE_FORBIDDEN_MESSAGE
          : error?.message || "Could not delete the reference contact."
      );
    } finally {
      setDeleteRow(null);
    }
  };

  const openEdit = (row: AiReferenceRow) => {
    setEditRow(row);
    setEditOpen(true);
  };

  const canViewAnswers = (row: AiReferenceRow) =>
    !!row.entryId &&
    (row.aiStatus === "COMPLETED" ||
      (row.aiStatus === "CLOSED" && !!row.completedAt));

  const renderAction = (row: AiReferenceRow) => {
    const status = row.aiStatus || "NEW";
    const isResend = RESENDABLE_STATUSES.includes(status);
    if (status === "NEW" || isResend) {
      const isSending = sendingId === row.referenceId && enqueueLoading;
      return (
        <button
          type="button"
          className={`refBtn ${isResend ? "refBtnNeutralOutline" : "refBtnBrand"}`}
          disabled={editPermissions || !row.email || enqueueLoading}
          title={
            editPermissions
              ? AI_REFERENCE_FORBIDDEN_MESSAGE
              : !row.email
                ? "Add an email to this reference first"
                : undefined
          }
          onClick={() => setConfirmRow(row)}
        >
          {isSending ? "Sending…" : isResend ? "Resend" : "Send"}
        </button>
      );
    }
    if (canViewAnswers(row)) {
      return (
        <button
          type="button"
          className="refBtn refBtnNeutralOutline"
          onClick={() => setAnswersRow(row)}
        >
          View Answers
        </button>
      );
    }
    // In flight: the API rejects a re-enqueue, so there is nothing to do.
    return <span className="refDash">--</span>;
  };

  const renderRowIcons = (row: AiReferenceRow) => {
    const inFlight = isInFlight(row);
    const processing = row.aiStatus === "CALL_COMPLETED";
    const editLabel = editPermissions
      ? AI_REFERENCE_FORBIDDEN_MESSAGE
      : inFlight
        ? "Can't edit while the AI Reference Taker is working on this reference"
        : "Edit";
    const deleteLabel = editPermissions
      ? AI_REFERENCE_FORBIDDEN_MESSAGE
      : processing
        ? "Answers are being prepared — try again in a few minutes"
        : "Delete";
    return (
      <>
        <RefIconButton
          label={row.entryId ? "View Trail" : "No history yet"}
          disabled={!row.entryId}
          onClick={() => setTrailRow(row)}
        >
          <HistoryIcon />
        </RefIconButton>
        <RefIconButton
          label={editLabel}
          disabled={editPermissions || inFlight}
          onClick={() => openEdit(row)}
        >
          <PenIcon />
        </RefIconButton>
        <RefIconButton
          label={deleteLabel}
          danger
          disabled={editPermissions || processing || deleteLoading}
          onClick={() => setDeleteRow(row)}
        >
          <TrashIcon />
        </RefIconButton>
      </>
    );
  };

  const deleteName = deleteRow?.referenceName || "this reference";

  return (
    <div className="refSection">
      <RefSectionHeader
        title="AI Reference Taker"
        count={rows.length}
        description="Automated outreach to reference contacts"
        action={
          <div className="refHeaderActions">
            {questionSet ? (
              <span
                className={`refQuestionsPill${questionSet.isCustom ? " refQuestionsPillCustom" : ""}`}
                title="Questions Grace asks this candidate's references"
              >
                {questionPillLabel(questionSet)}
              </span>
            ) : null}
            <button
              type="button"
              className="refBtn refBtnNeutralOutline"
              onClick={() => setQuestionsOpen(true)}
              disabled={editPermissions}
              title={
                editPermissions
                  ? AI_REFERENCE_FORBIDDEN_MESSAGE
                  : "Customize the reference questions for this candidate"
              }
            >
              <GearIcon />
              Questions
            </button>
            <button
              type="button"
              className="refBtn refBtnBrand"
              onClick={() => setAddOpen(true)}
              disabled={editPermissions}
            >
              + Add Reference Contact
            </button>
          </div>
        }
      />

      <div className="refTableScroll">
        <div className="refTable" role="table">
          <RefTableHead columns={AI_REFERENCE_COLUMNS} />

          {loading && !rows.length ? (
            <div className="refLoadingRow">
              <Loader loaderClass="sm-height " />
            </div>
          ) : null}

          {rows.map((row) => (
            <div
              key={row.referenceId}
              className="refGridRow refBodyRow"
              role="row"
            >
              <div role="cell">
                <RefNameCell
                  name={row.referenceName}
                  title={row.title}
                  company={row.company}
                  relationship={row.relationshipToCandidate}
                />
              </div>
              <div role="cell">
                <RefContactCell email={row.email} phone={row.phoneNumber} />
              </div>
              <div role="cell">
                <AiStatusPill row={row} />
              </div>
              <div role="cell">
                <RefDateCell
                  line1={lastActivityLine(row.lastActivityAt)}
                  line2={requestedByLine(row)}
                />
              </div>
              <div role="cell" className="refActionCell">
                {renderAction(row)}
                {renderRowIcons(row)}
              </div>
            </div>
          ))}

          {!loading && !rows.length && (
            <RefEmptyRow text="No reference contacts" />
          )}
        </div>
      </div>

      <AddReferenceContactModal
        isOpen={addOpen}
        toggle={() => setAddOpen(false)}
        candidateId={candidateId}
        onCreated={() => refetch()}
      />

      <AddReferenceContactModal
        isOpen={editOpen}
        toggle={() => setEditOpen(false)}
        candidateId={candidateId}
        onCreated={() => refetch()}
        editRow={editRow}
      />

      <ConfirmPopUp
        isOpen={!!deleteRow}
        toggle={() => {
          if (!deleteLoading) setDeleteRow(null);
        }}
        modalHeading="Delete Reference Contact"
        // ConfirmPopUp pads extraProp with spaces, which would put a gap
        // before the ":" / "?", so the whole sentence goes in confirmText.
        confirmText={
          isInFlight(deleteRow)
            ? `This stops the AI Reference Taker for ${deleteName}: no more emails will be sent and any booked call is cancelled. The contact is removed from both tables.`
            : `Delete ${deleteName}? The contact is removed from both tables.`
        }
        btnText="Delete"
        confirmAction={confirmDelete}
        loading={deleteLoading}
        disabled={deleteLoading}
      />

      <ConfirmPopUp
        isOpen={!!confirmRow}
        toggle={() => setConfirmRow(null)}
        modalHeading={
          confirmRow?.aiStatus &&
            RESENDABLE_STATUSES.includes(confirmRow.aiStatus)
            ? "Re-send to AI Reference Taker"
            : "Send to AI Reference Taker"
        }
        confirmText="The AI Reference Taker will email"
        extraProp={confirmRow?.referenceName || "this reference"}
        secondaryText={`(${confirmRow?.email ?? ""}) a link to schedule a short reference call. Continue?`}
        btnText="Yes, Send"
        confirmAction={confirmEnqueue}
        loading={enqueueLoading}
        disabled={enqueueLoading}
      />

      <AiReferenceQuestionsModal
        isOpen={questionsOpen}
        toggle={() => setQuestionsOpen(false)}
        candidateId={candidateId}
        questionSet={questionSet}
        loading={questionsLoading}
        loadError={questionsError?.message ?? null}
        forbiddenMessage={AI_REFERENCE_FORBIDDEN_MESSAGE}
        isForbiddenError={isForbiddenError}
      />

      <AiReferenceTrailModal row={trailRow} toggle={() => setTrailRow(null)} />

      <AiReferenceAnswersModal
        row={answersRow}
        toggle={() => setAnswersRow(null)}
      />
    </div>
  );
};

const AiReferenceSection = ({ candidateId, candidate }: any) => {
  // Default-deny while the feature query is in flight.
  const { data: moduleFeaturesData } = useQuery(MY_MODULE_FEATURES, {
    fetchPolicy: "cache-and-network",
  });
  const hasFeature: boolean = (
    moduleFeaturesData?.myModuleFeatures ?? []
  ).includes(AI_REFERENCE_FEATURE_KEY);

  if (!hasFeature || !candidateId) return null;
  return (
    <AiReferenceSectionBody candidateId={candidateId} candidate={candidate} />
  );
};

export default AiReferenceSection;
